
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Send, Eye, Save, FileText, Users, Mail, Edit3, Target } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { useSendBulkEmail } from '@/hooks/useBulkEmail';
import { useCanManageTemplates } from '@/hooks/useUserRole';
import { useSearchParams } from 'react-router-dom';
import { useInfluencer } from '@/domains/influencers/hooks/useInfluencer';
import { useCampaignInfluencers } from '@/hooks/useCampaignInfluencers';
import { isValidEmail } from '@/types/recipient.types';
import type { RecipientItem, InfluencerRecipient } from '@/types/recipient.types';
import RoleGuard from '@/components/auth/RoleGuard';
import RecipientSelector from './RecipientSelector';
import VariableInserter from './VariableInserter';
import EmailPreview from './EmailPreview';
import { HighlightedInput, HighlightedTextarea } from './HighlightedInputs';

const EmailComposer = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  const { toast } = useToast();
  const { data: templates = [] } = useEmailTemplates();
  const sendBulkEmail = useSendBulkEmail();
  const canManageTemplates = useCanManageTemplates();

  const [searchParams] = useSearchParams();
  const selectInfluencerId = searchParams.get('influencerId');
  const campaignId = searchParams.get('campaignId');

  // Direct single-influencer fetch
  const { data: preloadedInfluencer } = useInfluencer(selectInfluencerId || undefined);
  const { data: campaignInfluencers = [], isLoading: loadingCampaignInfluencers } =
    useCampaignInfluencers(campaignId || '');

  // Independent tracking flags
  const [hasAutoSelectedInfluencer, setHasAutoSelectedInfluencer] = useState(false);
  const [hasAutoSelectedCampaign, setHasAutoSelectedCampaign] = useState(false);

  // Flow A: Single Influencer Preselection (?influencerId=...)
  useEffect(() => {
    if (!selectInfluencerId || !preloadedInfluencer || hasAutoSelectedInfluencer) return;

    const rawEmail = preloadedInfluencer.email?.trim() || '';
    if (!isValidEmail(rawEmail)) {
      toast({
        title: 'Recipient Skipped',
        description: `"${preloadedInfluencer.realName || preloadedInfluencer.handle}" does not have a valid email on file.`,
        variant: 'destructive',
      });
      setHasAutoSelectedInfluencer(true);
      return;
    }

    const recipient: InfluencerRecipient = {
      id: preloadedInfluencer.id,
      sortName: preloadedInfluencer.realName || '',
      name: preloadedInfluencer.realName || preloadedInfluencer.handle,
      handle: preloadedInfluencer.handle,
      email: rawEmail,
      phone: preloadedInfluencer._db?.phone || '',
      platform: preloadedInfluencer.platforms?.[0] || 'instagram',
      avatar: preloadedInfluencer.profilePhoto || '',
      type: 'influencer',
      isDeliverable: true,
    };

    setSelectedRecipients((prev) => {
      if (prev.some((r) => r.id === recipient.id && r.type === 'influencer')) return prev;
      return [...prev, recipient];
    });
    setHasAutoSelectedInfluencer(true);
  }, [selectInfluencerId, preloadedInfluencer, hasAutoSelectedInfluencer, toast]);

  // Flow B: Campaign Influencers Preselection (?campaignId=...)
  useEffect(() => {
    if (!campaignId || campaignInfluencers.length === 0 || hasAutoSelectedCampaign || loadingCampaignInfluencers) return;

    const seen = new Set<string>();
    const deliverableRecipients: InfluencerRecipient[] = [];
    let skippedCount = 0;

    for (const ci of campaignInfluencers) {
      if (seen.has(ci.influencer_id)) continue;
      seen.add(ci.influencer_id);

      const email = ci.influencers?.email?.trim() || '';
      if (!isValidEmail(email)) {
        skippedCount++;
        continue;
      }

      deliverableRecipients.push({
        id: ci.influencer_id,
        sortName: ci.influencers?.name || '',
        name: ci.influencers?.name || ci.influencers?.handle || 'Unknown',
        handle: ci.influencers?.handle || '',
        email: email,
        phone: '',
        platform: ci.influencers?.platform || 'instagram',
        avatar: ci.influencers?.profile_pic_url || '',
        type: 'influencer',
        isDeliverable: true,
        campaignId: campaignId,
        campaign_id: campaignId,
      });
    }

    setSelectedRecipients((prev) => {
      const existingIds = new Set(prev.map((r) => r.id));
      const toAdd = deliverableRecipients.filter((r) => !existingIds.has(r.id));
      return [...prev, ...toAdd];
    });
    setHasAutoSelectedCampaign(true);

    if (deliverableRecipients.length > 0) {
      toast({
        title: 'Campaign Influencers Selected',
        description: `${deliverableRecipients.length} influencer(s) pre-selected${
          skippedCount > 0 ? ` (${skippedCount} skipped without valid email)` : ''
        }.`,
      });
    } else if (skippedCount > 0) {
      toast({
        title: 'No Deliverable Recipients',
        description: `None of the ${skippedCount} campaign influencer(s) have a valid email on file.`,
        variant: 'destructive',
      });
    }
  }, [campaignId, campaignInfluencers, hasAutoSelectedCampaign, loadingCampaignInfluencers, toast]);

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setSelectedTemplate(templateId);
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !body.trim()) {
      toast({
        title: "Missing Content",
        description: "Please fill in both subject and body.",
        variant: "destructive",
      });
      return;
    }

    if (selectedRecipients.length === 0) {
      toast({
        title: "No Recipients",
        description: "Please select at least one recipient.",
        variant: "destructive",
      });
      return;
    }

    try {
      await sendBulkEmail.mutateAsync({
        subject,
        body,
        recipients: selectedRecipients,
        template_id: selectedTemplate || undefined
      });

      // Reset form
      setSubject('');
      setBody('');
      setSelectedRecipients([]);
      setSelectedTemplate('');
    } catch (error) {
      console.error('Error sending bulk email:', error);
    }
  };

  const insertVariableIntoSubject = (variable: string) => {
    const input = document.getElementById('emailSubject') as HTMLInputElement;
    if (input) {
      const cursorPos = input.selectionStart || 0;
      const textBefore = subject.substring(0, cursorPos);
      const textAfter = subject.substring(cursorPos);
      const newText = textBefore + `{{${variable}}}` + textAfter;

      setSubject(newText);

      // Set cursor position after the inserted variable
      setTimeout(() => {
        input.focus();
        const newCursorPos = cursorPos + variable.length + 4; // 4 for the {{}}
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const insertVariableIntoBody = (variable: string) => {
    const textarea = document.getElementById('emailBody') as HTMLTextAreaElement;
    if (textarea) {
      const cursorPos = textarea.selectionStart || 0;
      const textBefore = body.substring(0, cursorPos);
      const textAfter = body.substring(cursorPos);
      const newText = textBefore + `{{${variable}}}` + textAfter;

      setBody(newText);

      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = cursorPos + variable.length + 4; // 4 for the {{}}
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handlePreview = () => {
    if (selectedRecipients.length > 0) {
      setShowPreview(true);
    } else {
      toast({
        title: "No Recipients",
        description: "Please select recipients to preview the email.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-gradient-to-r from-purple-50 to-blue-50">
        {/* <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl text-gray-900">Compose Bulk Email</CardTitle>
              <CardDescription className="text-gray-600">
                Send personalized emails to multiple recipients
              </CardDescription>
            </div>
          </div>
        </CardHeader> */}
      </Card>

      <Tabs defaultValue="compose" className="space-y-6">
        <div className="inline-flex w-auto bg-white rounded-lg border border-gray-200 p-1">
          <TabsList className="inline-flex h-12 w-auto items-stretch bg-transparent p-0">
            <TabsTrigger
              value="compose"
              className="group relative flex items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!bg-transparent data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-4 rounded-none"
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <Edit3 className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Compose</span>
                <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-[#A655F8] opacity-0 group-data-[state=active]:opacity-100" />
              </div>
            </TabsTrigger>

            <div className="h-6 w-[1px] bg-slate-200 self-center shrink-0" />

            <TabsTrigger
              value="recipients"
              className="group relative flex items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!bg-transparent data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-4 rounded-none"
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <Target className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Recipients ({selectedRecipients.length})</span>
                <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-[#A655F8] opacity-0 group-data-[state=active]:opacity-100" />
              </div>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="compose" className="space-y-6">
          {/* Template Selection */}
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-gray-900">Choose Template (Optional)</CardTitle>
                  <CardDescription>
                    Start with a pre-made template or create from scratch
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger className="border-gray-200 focus:border-purple-500 focus:ring-purple-500">
                  <SelectValue placeholder="Select a template or start from scratch" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {!canManageTemplates && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Only admins and talent managers can create new templates
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Content */}
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Edit3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-gray-900">Email Content</CardTitle>
                  <CardDescription>
                    Write your email content with personalization variables
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="emailSubject" className="text-sm font-medium text-gray-700">Subject Line</Label>
                  <VariableInserter
                    onInsert={insertVariableIntoSubject}
                    buttonText="Insert Variable"
                  />
                </div>
                <HighlightedInput
                  id="emailSubject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="emailBody" className="text-sm font-medium text-gray-700">Email Body</Label>
                  <VariableInserter
                    onInsert={insertVariableIntoBody}
                    buttonText="Insert Variable"
                  />
                </div>
                <HighlightedTextarea
                  id="emailBody"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your email content here... Use {{variable_name}} for personalization."
                  minHeight="300px"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="border border-gray-200 shadow-sm bg-gradient-to-r from-green-50 to-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{selectedRecipients.length} recipients selected</span>
                  </div>
                  {subject.trim() && body.trim() && (
                    <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                      Ready to send
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={!subject.trim() || !body.trim() || selectedRecipients.length === 0}
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>

                  <Button
                    variant="default"
                    onClick={handleSendEmail}
                    disabled={sendBulkEmail.isPending || !subject.trim() || !body.trim() || selectedRecipients.length === 0}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendBulkEmail.isPending ? 'Sending...' : `Send to ${selectedRecipients.length} Recipients`}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recipients">
          <RecipientSelector
            selectedRecipients={selectedRecipients}
            onRecipientsChange={setSelectedRecipients}
          />
        </TabsContent>
      </Tabs>

      {/* Email Preview Modal */}
      {showPreview && (
        <EmailPreview
          subject={subject}
          body={body}
          recipients={selectedRecipients}
          campaignId={campaignId}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default EmailComposer;
